import LeftArrowIcon from "assets/images/LeftArrowIcon";
import RightArrowIcon from "assets/images/RightArrowIcon";
import classNames from "classnames";
import {isSameDay, getDaysInMonth, isBefore, add} from "date-fns";
import {useEffect, useState} from "react";

import Button from "components/Button";
import DateButton from "components/DatePicker/DateButton";
import {prevMonth, nextMonth, monthToName} from "components/DatePicker/helpers";

import styles from "./DatePicker.module.css";

type Props = {
  className?: string;
  datePicked: Date | undefined;
  onDateChange: (date: Date) => void;
  availableDates?: Date[];
  arePreviousDatesDisabled?: boolean;
  enableCurrentDate?: boolean;
  isDateAvailable?: (dateToCheck: Date) => boolean;
};

export default function DatePicker({
  datePicked,
  onDateChange,
  availableDates,
  arePreviousDatesDisabled,
  enableCurrentDate,
  isDateAvailable,
  ...props
}: Props) {
  const todaysDate = new Date();
  const [currentMonth, setCurrentMonth] = useState(
    (datePicked ?? todaysDate).getMonth(),
  );
  const [currentYear, setCurrentYear] = useState(
    (datePicked ?? todaysDate).getFullYear(),
  );
  const [numDaysInMonth, setNumDaysInMonth] = useState(
    getDaysInMonth(new Date(currentYear, currentMonth)),
  );

  const daysOfWeek = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  useEffect(() => {
    setNumDaysInMonth(getDaysInMonth(new Date(currentYear, currentMonth)));
  }, [currentMonth, currentYear]);

  return (
    <div className={classNames(styles.datePickerContainer, props.className)}>
      <div className={styles.datePicker}>
        <div className={styles.topMonthYearRow}>
          <Button
            className={styles.monthButton}
            iconLeft={(color: string) => <LeftArrowIcon color={color} />}
            variant="minimal"
            disabled={
              currentMonth === todaysDate.getMonth() &&
              currentYear === todaysDate.getFullYear() &&
              availableDates !== undefined
            }
            onClick={() => {
              setCurrentMonth((prev) => {
                if (prev === 0) {
                  setCurrentYear(currentYear - 1);
                }
                return prevMonth(prev);
              });
            }}
          >
            {monthToName[prevMonth(currentMonth)].slice(0, 3)}
          </Button>
          <span className={styles.titleText}>
            {monthToName[currentMonth] + " " + currentYear}
          </span>
          <Button
            className={styles.monthButton}
            iconRight={(color: string) => <RightArrowIcon color={color} />}
            variant="minimal"
            onClick={() => {
              setCurrentMonth((prev) => {
                if (prev === 11) {
                  setCurrentYear(currentYear + 1);
                }
                return nextMonth(prev);
              });
            }}
          >
            {monthToName[nextMonth(currentMonth)].slice(0, 3)}
          </Button>
        </div>

        <div className={styles.daysOfWeekRow}>
          {daysOfWeek.map((day) => (
            <span key={day} className={styles.dayOfWeek}>
              {day}
            </span>
          ))}
        </div>

        <div className={styles.daysOfMonth}>
          {Array(numDaysInMonth)
            .fill(0)
            .map((_, index) => {
              const currentDate = new Date();
              const date = new Date(currentYear, currentMonth, index + 1);
              let isAvailable =
                availableDates?.some((availableDate) =>
                  isSameDay(availableDate, date),
                ) ?? true;
              if (isAvailable && arePreviousDatesDisabled) {
                isAvailable = enableCurrentDate
                  ? isBefore(currentDate, add(date, {days: 1}))
                  : isBefore(currentDate, date);
              }
              if (isAvailable && isDateAvailable) {
                isAvailable = isAvailable && isDateAvailable(date);
              }
              const isSelected = datePicked
                ? isSameDay(datePicked, date)
                : false;
              return (
                <DateButton
                  key={`${currentYear} ${currentMonth} ${date} ${isSelected}`}
                  id={`${currentYear} ${currentMonth} ${date}`}
                  dateLabel={date.getDate()}
                  day={date.getDay()}
                  isAvailable={isAvailable}
                  isSelected={isSelected}
                  onChange={(clickedDay: number) => {
                    const newDate = new Date(
                      currentYear,
                      currentMonth,
                      clickedDay,
                    );
                    onDateChange(newDate);
                  }}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
}
